import { useState } from "react";
import { useParams, useHistory } from "react-router";
import { Link } from "react-router-dom";

import { Paper, useMediaQuery, useTheme } from "@material-ui/core";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteIcon from "@material-ui/icons/Delete";

import { editProductTabs } from "../../../../constants";
import { useGetProductById } from "../../../../utils/react-query/queryEcommerce/queries";
import { useDeleteProduct } from "../../../../utils/react-query/queryInventory/queries";
import { useMyPermissions } from "../../../../utils/react-query/queryRbac/queries";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { successToast, errorToast } from "../../../../services/toasts";

import styles from "./productEditPage.module.scss";
import LoadingScreen from "../../../../components/SplitScreen/LoadingScreen";
import {
  GeneralInfoForm,
  ImagesForm,
  MeasuresForm,
  PackagingMeasuresForm,
  TechnicalSheetForm,
  ComplementaryForm,
} from "./components";

const ProductEditPage = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("general");
  const { data: product, isLoading } = useGetProductById(+id);

  const { mutateAsync: executeDelete } = useDeleteProduct();
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  const canDeleteProduct =
    !permissionsLoading && permissions.has("inventory:delete_product");

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const handleDelete = async () => {
    try {
      await executeDelete(+id);
      setDeleteConfirmDialog({ ...deleteConfirmDialog, isOpen: false });
      successToast("Producto eliminado exitosamente");
      history.push("/proveedor/inventario");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al eliminar el producto";
      errorToast(message);
    }
  };

  // Function to change the active tab
  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!product) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Paper className={styles.productEditNav}>
        <div className={styles.productEditContent}>
          <Link to="/proveedor/inventario">
            <div className={styles.btnBack}>
              <ArrowBackIcon fontSize="small" />
            </div>
          </Link>
          <div className={styles.productEditText}>
            <span>Regresar al inventario</span>
            <h4>Editar Producto</h4>
          </div>
        </div>

        <Tabs
          className={styles.productEditTabs}
          value={activeTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobileOrTablet ? "scrollable" : "standard"}
          scrollButtons="on"
          centered
        >
          {editProductTabs.map((editTab) => (
            <Tab
              key={editTab.id}
              label={editTab.label}
              value={editTab.id}
              style={{ minWidth: "auto", textTransform: "capitalize" }}
            />
          ))}
        </Tabs>
      </Paper>

      <div className="layout-dashboard-container">
        {activeTab === "general" && <GeneralInfoForm product={product} />}
        {activeTab === "measurements" && (
          <MeasuresForm
            dimensions={product.dimensions ?? null}
            categoryId={product.category_id}
            ProductId={product.product_id}
          />
        )}
        {activeTab === "packagingMasures" && (
          <PackagingMeasuresForm product={product} />
        )}
        {activeTab === "images" && (
          <ImagesForm
            images={product.ambient_images ?? []}
            principalImage={product.principal_image ?? []}
            productId={product.product_id}
          />
        )}
        {activeTab === "technicalSheet" && (
          <TechnicalSheetForm
            technicalSheet={product.technical_sheet ?? []}
            productId={product.product_id}
          />
        )}
        {activeTab === "complementary" && (
          <ComplementaryForm productId={product.product_id} />
        )}

        {canDeleteProduct && (
          <div className={styles.dangerZone}>
            <h4>Zona de peligro</h4>
            <p>
              Esta acción eliminará el producto de la tienda. Se puede deshacer
              desde la base de datos.
            </p>
            <button
              className={styles.btnDeleteProduct}
              onClick={() =>
                setDeleteConfirmDialog({
                  isOpen: true,
                  title: "Eliminar producto",
                  subTitle: `¿Estás seguro que deseas eliminar "${product.name}"? Esta acción se puede deshacer.`,
                  onConfirm: handleDelete,
                })
              }
            >
              <DeleteIcon fontSize="small" />
              Eliminar producto
            </button>
          </div>
        )}
      </div>

      <DeleteConfirmationDialog
        confirmDialog={deleteConfirmDialog}
        setConfirmDialog={setDeleteConfirmDialog}
      />
    </>
  );
};

export default ProductEditPage;
